/**
 * Phase 87av — Liste des courriers corporate (Maison Thaï).
 * Route : /courriers (admin)
 *
 * Vue "cartes" (choix UX 1b) — chaque courrier est représenté par une carte
 * compacte avec aperçu du sujet, badge type, destinataire, date et actions
 * rapides (dupliquer / archiver / supprimer).
 */
import React, { useEffect, useMemo, useState } from "react";
import { LETTER_TEMPLATES, templatePayload } from "../lib/letterTemplates";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Plus, Search, Loader2, Mail, User, Building2, Calendar,
  Copy, Trash2, Archive, ArchiveRestore, Eye, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-CH", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

// Mapping type → palette (clé majuscule sans accent). Fallback doré si inconnu.
const TYPE_PALETTE = {
  INFORMATION: { bg: "#EAF4EE", fg: "#1F4E3D", border: "#C5DBCF" },
  AVERTISSEMENT: { bg: "#FCEEEA", fg: "#8B1A1A", border: "#E8C1B4" },
  COMMUNICATION: { bg: "#FBF4E9", fg: "#8C6B2F", border: "#E8D9B8" },
  DISTRIBUTEUR: { bg: "#FDF3E0", fg: "#8C6B2F", border: "#C1A063" },
  RAPPEL: { bg: "#F0F0EB", fg: "#735524", border: "#E5E5E0" },
  ANNONCE: { bg: "#EAF1F8", fg: "#2E5C8A", border: "#C5D9E8" },
};
const DEFAULT_PALETTE = { bg: "#FBF4E9", fg: "#8C6B2F", border: "#E8D9B8" };

const paletteForType = (raw) => {
  if (!raw) return DEFAULT_PALETTE;
  const key = String(raw).toUpperCase();
  // Cherche le préfixe le plus long qui matche (ex: "AVERTISSEMENT REVENDEUR" matche AVERTISSEMENT)
  for (const k of Object.keys(TYPE_PALETTE)) {
    if (key.includes(k)) return TYPE_PALETTE[k];
  }
  return DEFAULT_PALETTE;
};

export default function CompanyLetters() {
  const confirm = useConfirm();
  const [letters, setLetters] = useState(null);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [filterType, setFilterType] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/company-letters?archived=${showArchived ? "true" : "false"}`);
      setLetters(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement courriers");
      setLetters([]);
    }
  };

  useEffect(() => { load(); }, [showArchived]);

  const createNew = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/company-letters", {
        letter_type: "Information",
        subject: "",
        body_json: { type: "doc", content: [{ type: "paragraph" }] },
        body_plain: "",
      });
      window.location.href = `/courriers/${data.id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur création");
      setCreating(false);
    }
  };

  const createFromTemplate = async (tpl) => {
    setCreating(true);
    try {
      const { data } = await api.post("/company-letters", templatePayload(tpl));
      window.location.href = `/courriers/${data.id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur création");
      setCreating(false);
    }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/company-letters/${id}/duplicate`);
      toast.success(`Courrier dupliqué — ${data.reference}`);
      window.location.href = `/courriers/${data.id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur duplication");
    }
  };

  const toggleArchive = async (letter) => {
    try {
      await api.put(`/company-letters/${letter.id}`, { archived: !letter.archived });
      toast.success(letter.archived ? "Courrier désarchivé" : "Courrier archivé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const removeLetter = async (letter) => {
    const ok = await confirm({
      title: `Supprimer ${letter.reference} ?`,
      description: "Action irréversible. Le courrier sera définitivement effacé.",
      confirmText: "Supprimer",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/company-letters/${letter.id}`);
      toast.success("Courrier supprimé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur suppression");
    }
  };

  // Liste des types distincts (pour le filtre dynamique)
  const distinctTypes = useMemo(() => {
    const set = new Set();
    (letters || []).forEach((l) => { if (l.letter_type) set.add(l.letter_type); });
    return Array.from(set).sort();
  }, [letters]);

  const filtered = (letters || []).filter((l) => {
    if (filterType && (l.letter_type || "") !== filterType) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (l.reference || "").toLowerCase().includes(s) ||
      (l.subject || "").toLowerCase().includes(s) ||
      (l.recipient_name || "").toLowerCase().includes(s) ||
      (l.recipient_company || "").toLowerCase().includes(s) ||
      (l.body_plain || "").toLowerCase().includes(s) ||
      (l.letter_type || "").toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-6" data-testid="company-letters-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1C1A]">Courriers corporate</h1>
          <p className="text-sm text-[#575A57] mt-1">
            Communications officielles avec PDF norme suisse (information, mise en garde, vending…)
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
        {LETTER_TEMPLATES.map((tpl) => (
          <Button
            key={tpl.key}
            variant="outline"
            onClick={() => createFromTemplate(tpl)}
            disabled={creating}
            data-testid={`company-letter-template-${tpl.key}`}
          >
            <FileText className="w-4 h-4 mr-2" /> Modèle : {tpl.label}
          </Button>
        ))}
        <Button
          onClick={createNew}
          disabled={creating}
          className="bg-[#1F4E3D] hover:bg-[#173B2E]"
          data-testid="company-letter-create"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          Nouveau courrier
        </Button>
        </div>
      </div>

      <Card className="border-[#E5E5E0]">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A3A6A3]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Référence, sujet, destinataire, corps…"
              className="pl-9"
              data-testid="company-letter-search"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border border-[#E5E5E0] rounded px-3 py-2 text-sm bg-white"
            data-testid="company-letter-filter-type"
          >
            <option value="">Tous types</option>
            {distinctTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-[#575A57] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="company-letter-show-archived"
            />
            Afficher les archivés
          </label>
        </CardContent>
      </Card>

      {letters === null && (
        <div className="flex items-center gap-2 text-sm text-[#575A57]">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      )}

      {letters && filtered.length === 0 && (
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-8 text-center text-sm text-[#575A57]" data-testid="company-letters-empty">
            {search || filterType ? "Aucun courrier trouvé avec ces filtres." : (showArchived ? "Aucun courrier archivé." : "Aucun courrier pour le moment. Créez-en un avec « Nouveau courrier ».")}
          </CardContent>
        </Card>
      )}

      {filtered.length > 0 && (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((l) => {
            const palette = paletteForType(l.letter_type);
            const previewText = (l.body_plain || "").replace(/\s+/g, " ").trim().slice(0, 160);
            return (
              <Card
                key={l.id}
                className="border-[#E5E5E0] hover:border-[#1F4E3D] transition-colors flex flex-col"
                data-testid={`company-letter-card-${l.reference}`}
              >
                <CardContent className="p-4 flex flex-col flex-1 gap-3">
                  {/* Header carte */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-semibold text-[#1A1C1A]">{l.reference}</span>
                        {l.archived && (
                          <Badge className="bg-[#F0F0EB] text-[#737373] border-[#E5E5E0] text-[10px] uppercase">Archivé</Badge>
                        )}
                      </div>
                      <div className="mt-1.5">
                        <Badge
                          style={{ backgroundColor: palette.bg, color: palette.fg, borderColor: palette.border }}
                          className="text-[10px] uppercase tracking-wider border"
                        >
                          {l.letter_type || "Sans type"}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Sujet */}
                  <div>
                    <p className="text-sm font-semibold text-[#1A1C1A] leading-snug line-clamp-2 min-h-[2.5em]">
                      {l.subject || <span className="text-[#A3A6A3] italic">(Pas de sujet)</span>}
                    </p>
                  </div>

                  {/* Destinataire + date */}
                  <div className="text-xs text-[#575A57] space-y-1">
                    <div className="flex items-center gap-1.5 truncate">
                      {l.recipient_company ? <Building2 className="w-3 h-3 shrink-0" /> : <User className="w-3 h-3 shrink-0" />}
                      <span className="truncate">
                        {l.recipient_company || l.recipient_name || <span className="italic text-[#A3A6A3]">Destinataire non défini</span>}
                      </span>
                    </div>
                    {l.recipient_city && (
                      <div className="text-[11px] text-[#A3A6A3] truncate">
                        {l.recipient_postal_code} {l.recipient_city}
                      </div>
                    )}
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" />
                      <span>{fmtDate(l.letter_date)}</span>
                    </div>
                  </div>

                  {/* Aperçu corps */}
                  {previewText && (
                    <p className="text-xs text-[#737373] italic line-clamp-3 leading-relaxed bg-[#FAFAF7] p-2 rounded border border-[#E5E5E0]">
                      {previewText}{(l.body_plain || "").length > 160 ? "…" : ""}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="mt-auto pt-2 flex items-center gap-1 border-t border-[#E5E5E0]">
                    <Link to={`/courriers/${l.id}`} className="flex-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full text-xs"
                        data-testid={`company-letter-open-${l.reference}`}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1.5" /> Ouvrir
                      </Button>
                    </Link>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => duplicate(l.id)}
                      className="text-[#575A57] hover:text-[#1F4E3D]"
                      title="Dupliquer"
                      data-testid={`company-letter-duplicate-${l.reference}`}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleArchive(l)}
                      className="text-[#575A57] hover:text-[#8C6B2F]"
                      title={l.archived ? "Désarchiver" : "Archiver"}
                      data-testid={`company-letter-archive-${l.reference}`}
                    >
                      {l.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeLetter(l)}
                      className="text-[#C05C47] hover:bg-[#FCEEEA]"
                      title="Supprimer"
                      data-testid={`company-letter-delete-${l.reference}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
